import React, { useMemo, useState, useEffect } from "react"
import { Col, Row, Button } from "reactstrap"
import { useLocation } from "react-router-dom"
import moment from "moment"

import { EvaluationSidebar } from "./Components/EvaluationSidebar"
import EvaluationCard from "./Components/evaluationCard"
import ExtraEventCard from "./Components/ExtraEventCard"
import { useEvaluationData } from "./Hooks/useEvaluationData"
import { toISODate } from "../../utils/date"
import { ContentLoader } from "../../components/Common"

const occursOnDate = (schedule, isoDate) => {
  const sessionDate = schedule?.sessionDate || null
  if (sessionDate) {
    // Compara diretamente a string "YYYY-MM-DD" para evitar bugs de timezone no JS
    return String(sessionDate).slice(0, 10) === String(isoDate).slice(0, 10)
  }
  return false
}

/**
 * Página de Avaliação de Metodologia.
 * Usa useEvaluationData para buscar sessões e dados (sem GradeContext).
 */
const EvaluationPage = () => {
  const location = useLocation()

  // 1. Data de referência local (sem depender de GradeContext)
  const [referenceDate, setReferenceDate] = useState(new Date())

  // 2. Dados da avaliação (sessões, employees, atividades)
  const { sessions, employees, loading } = useEvaluationData({ referenceDate })

  // 3. Estados Locais da Página de Avaliação
  const [selectedSchedule, setSelectedSchedule] = useState(null)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("")
  const [activeTab, setActiveTab] = useState(
    location.pathname.includes("tests") ? "performance" : "technical"
  )

  // Sync tab with URL changes
  useEffect(() => {
    setActiveTab(location.pathname.includes("tests") ? "performance" : "technical")
  }, [location.pathname])

  useEffect(() => {
    const title = location.pathname.includes("tests") ? "Testes de Performance" : "Avaliação Técnica"
    document.title = `${title} | NeoApp`
  }, [location.pathname])

  // Filtro de sessões do dia (sem filtro de professor) para extrair os professores ativos
  const todayAllSchedules = useMemo(() => {
    const todayISO = toISODate(referenceDate)
    return (sessions || []).filter(schedule => schedule && occursOnDate(schedule, todayISO))
  }, [sessions, referenceDate])

  // Filtro final de sessões a exibir, aplicando o filtro de professor
  const todaySchedules = useMemo(() => {
    return todayAllSchedules
      .filter(schedule => !selectedEmployeeId || String(schedule.idEmployee) === String(selectedEmployeeId))
      .sort((a, b) => (a.startTime || "00:00").localeCompare(b.startTime || "00:00"))
  }, [todayAllSchedules, selectedEmployeeId])

  // Scroll to top when schedule is selected
  useEffect(() => {
    if (selectedSchedule) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedSchedule]);

  const activeEmployees = useMemo(() => {
    const activeEmployeeIds = new Set(todayAllSchedules.map(s => String(s.idEmployee)))
    return [...(employees || [])]
      .filter(emp => activeEmployeeIds.has(String(emp.id)))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
  }, [employees, todayAllSchedules])

  const handlePrevDay = () => {
    setReferenceDate(prev => moment(prev).subtract(1, 'days').toDate())
    setSelectedSchedule(null)
  }

  const handleNextDay = () => {
    setReferenceDate(prev => moment(prev).add(1, 'days').toDate())
    setSelectedSchedule(null)
  }

  if (loading) {
    return <ContentLoader />
  }

  return (
    <div className="container-fluid p-0 p-md-2">
      <Row className="g-2 g-md-4">
        {/* Lista Lateral de Turmas */}
        <Col xs="12" md="3" lg="3" className={selectedSchedule ? "d-none d-md-block" : ""}>
          <EvaluationSidebar
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            selectedEmployeeId={selectedEmployeeId}
            setSelectedEmployeeId={setSelectedEmployeeId}
            employees={activeEmployees}
            currentDate={referenceDate}
            handlePrevDay={handlePrevDay}
            handleNextDay={handleNextDay}
            todaySchedules={todaySchedules}
            selectedSchedule={selectedSchedule}
            setSelectedSchedule={setSelectedSchedule}
          />
        </Col>

        {/* Card de Avaliação / Extras */}
        <Col xs="12" md="9" lg="9" className={!selectedSchedule ? "d-none d-md-block" : ""}>
          <div className="d-md-none mb-3">
            <Button color="light" className="w-100 shadow-sm border-0 fw-medium" onClick={() => setSelectedSchedule(null)}>
              <i className="mdi mdi-arrow-left me-2"></i> Voltar para Lista de Turmas
            </Button>
          </div>

          {activeTab === "extras" ? (
            <ExtraEventCard schedule={selectedSchedule} />
          ) : (
            <EvaluationCard
              schedule={selectedSchedule}
              activeMode={activeTab}
            />
          )}
        </Col>
      </Row>
    </div>
  )
}

export default EvaluationPage